import { AgTechHttpRequest } from "@agtech/core/data/actions/AgTechHttpRequests";
import { useAgTechApiDownloadRequest } from "@agtech/core/data/http/AgTechApiGetRequests";
import { SaleLot } from "../lots/LotData";
import { BidderLotSaleReadyForInvoicing, InvoiceDetails, InvoiceInfo, InvoiceStatus } from "./InvoicingData";
import { Payment } from "./payments/InvoicePaymentData";
import { useCurrentSale } from "../sales/SaleDataStore";
import { useBidderDataGetContactBiddersQuery, useBidderDataGetSaleBidderDataQuery } from "../bidders/BidderDataQueries";
import { AgTechQuery, useAgTechQuery, useAgTechQueryCombination, useAgTechQueryResultCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { LotDataGetLotsQuery } from "../lots/LotDataQueries";
import { ContactCredit, getAvailableContactCredits, getAvailableCreditsForInvoice, getNonExpiredCredits } from "../operation/contacts/credits/ContactCreditData";
import { SaleInvoiceModifier } from "../sales/InvoiceModifiers/SaleInvoiceModifierData";
import { SaleContactDataGetContactDetailsQuery } from "../operation/contacts/SaleContactDataQueries";
import { AgTechInvalidateQueryDataProps } from "@agtech/core/data/actions/AgTechDataActions";
import { InvoiceDataGetInvoiceLotsQuery } from "../lots/invoices/LotInvoiceDataQueries";

export const InvoiceQueryKey = 'Invoices';

// Invoicing DTOs

type InvoiceListDataDTO = {
    id: number,
    invoiceNumber: string,
    invoiceTotal: number,
    numberOfLots: number,
    numberOfInvoiceModifiersApplied?: number,
    numberOfCreditsApplied?: number,
    purchaserName: string,
    invoiceDate: Date,
    contactId: number
}

type InvoiceDetailsDTO = {
    invoiceId: number,
    contactId: number,
    invoiceDate: string,
    invoiceNumber: string,
    invoiceTotal: number,
    credits: ContactCredit[],
    invoiceModifiers: SaleInvoiceModifier[],
    isPayed: boolean,
    timestamp: string,
    lots: {
        lotId: number,
        bidderId: number,
        invoiceId: number,
        saleId: number
    }[],
    payments: Payment[],
}

type BidderLotsToInvoiceSummaryDTO = {
    bidderId: number,
    bidderName: string,
    bidderNumber: string,
    contactId?: number,
    numberOfLots: number,
    totalPurchasePrice: number
}

type BidderLotToInvoiceDTO = {
    lotId: number,
    lotNumber: string,
    lotDescription: string,
    purchasePrice: number,
    percentage: number,
    lotTimestamp: string,
}

///
/// Queries
///

export const InvoiceDataGetNextInvoiceNumberQuery: AgTechQuery<{ saleId: number }, number> = {
    queryKey: () => [InvoiceQueryKey, 'NextInvoiceNumber'],
    query: (props) => {
        return props.http.getEntity({
            request: 'Invoice/GetInvoiceNumber/' + props.inputData.saleId
        });
    },
    content: {
        loadingText: 'Generating next invoice number...',
        failureText: 'Failed to generate next invoice number.'
    },
    settings: () => ({
        staleTime: 0 // No caching
    })
}

export const InvoiceDataGetUnprocessedSalesQuery: AgTechQuery<{ saleId: number }, BidderLotsToInvoiceSummaryDTO[]> = {
    queryKey: () => [InvoiceQueryKey, 'UnprocessedSales'],
    query: (props) => {
        return props.http.getEntity({
            request: 'Lot/Unprocessed/' + props.inputData.saleId
        });
    },
    content: {
        loadingText: 'Loading unprocessed sales...',
        failureText: 'Failed to load unprocessed sales.'
    }
}

export const InvoiceDataGetUnpaidInvoicesQuery: AgTechQuery<{ saleId: number }, InvoiceListDataDTO[]> = {
    queryKey: () => [InvoiceQueryKey, 'UnpaidInvoices'],
    query: (props) => {
        return props.http.getEntity<InvoiceListDataDTO[]>({
            request: 'Invoice/GetUnpaidInvoices/' + props.inputData.saleId
        });
    },
    content: {
        loadingText: 'Loading unpaid invoices...',
        failureText: 'Failed to load unpaid invoices.'
    }
}

export const InvoiceDataGetPaidInvoicesQuery: AgTechQuery<{ saleId: number }, InvoiceListDataDTO[]> = {
    queryKey: () => [InvoiceQueryKey, 'PaidInvoices'],
    query: (props) => {
        return props.http.getEntity<InvoiceListDataDTO[]>({
            request: 'Invoice/GetPaidInvoices/' + props.inputData.saleId
        });
    },
    content: {
        loadingText: 'Loading paid invoices...',
        failureText: 'Failed to load paid invoices.'
    }
}

export const InvoiceDataGetBidderLotsToInvoiceQuery: AgTechQuery<{ bidderId: number}, BidderLotToInvoiceDTO[]> = {
    queryKey: ({ bidderId }) => ['BidderLotsToInvoice', bidderId],
    query: (props) => {
        return props.http.getEntity({
            request: 'Lot/GetForInvoicingByBidder/' + props.inputData.bidderId
        });
    },
    content: {
        loadingText: 'Loading lots to invoice...',
        failureText: 'Failed to load lots to invoice.'
    },
    settings: () => ({
        staleTime: 0
    })
}

export const InvoiceDataGetInvoiceDocumentQuery: AgTechQuery<{ invoiceId: number }, Blob> = {
    queryKey: ({ invoiceId }) => [InvoiceQueryKey, 'InvoiceDocument', invoiceId],
    query: (props) => {
        return props.http.getFile({
            path: 'Invoice/GetInvoiceDocument/' + props.inputData.invoiceId
        });
    },
    content: {
        loadingText: 'Downloading invoice document...',
        failureText: 'Failed to download invoice document.'
    }
}

const InvoiceDataGetInvoiceDetailQuery: AgTechQuery<{ saleId: number, invoiceId: number }, InvoiceDetailsDTO> = {
    queryKey: ({ invoiceId }) => [InvoiceQueryKey, 'InvoiceDetails', invoiceId],
    query: (props) => {
        return props.http.getEntity({
            request: 'Invoice/GetInvoiceDetails/' + props.inputData.saleId + '/' + props.inputData.invoiceId
        });
    },
    content: {
        loadingText: 'Loading invoice details...',
        failureText: 'Failed to load invoice details.'
    }
}

///
/// Composite Queries
///

export const useInvoiceDataGetBidderLotsToInvoiceQuery = (props: { bidderId: number }) => {
    const currentSale = useCurrentSale();
    
    return useAgTechQueryCombination({
        firstQuery: {
            query: LotDataGetLotsQuery,
            queryContext: { saleId: currentSale.id }
        },
        secondQuery: {
            query: InvoiceDataGetBidderLotsToInvoiceQuery,
            queryContext: props
        },
        projection: (lots, lotsToInvoice) => lotsToInvoice.map<SaleLot>(invoiceLot => {
            const lot = lots[invoiceLot.lotId];

            return {
                ...lot,
                purchasePrice: (lot.purchasePrice ?? 0) * invoiceLot.percentage
            };
        })
    });
}

export const useInvoiceDataGetUnProcessedSalesQuery = () => {
    const currentSale = useCurrentSale();

    const unprocessedSalesQuery = useAgTechQuery({
        query: InvoiceDataGetUnprocessedSalesQuery,
        queryContext: { saleId: currentSale.id }
    });

    const bidderDataQuery = useBidderDataGetSaleBidderDataQuery();
    
    return useAgTechQueryResultCombination({
        firstQuery: bidderDataQuery,
        secondQuery: unprocessedSalesQuery,
        projection: (bidderData, unprocessedSales) => unprocessedSales.map<BidderLotSaleReadyForInvoicing>(sale => ({
            id: sale.bidderId,
            bidder: bidderData.bidders[sale.bidderId],
            numberOfLots: sale.numberOfLots,
            totalPurchasePrice: sale.totalPurchasePrice
        }))
    })
}

export const useInvoiceDataGetUnpaidInvoicesQuery = () => {
    const currentSale = useCurrentSale();

    const getContactBiddersQuery = useBidderDataGetContactBiddersQuery();

    const getUnpaidInvoicesQuery = useAgTechQuery({
        query: InvoiceDataGetUnpaidInvoicesQuery,
        queryContext: { saleId: currentSale.id }
    });

    return useAgTechQueryResultCombination({
        firstQuery: getContactBiddersQuery,
        secondQuery: getUnpaidInvoicesQuery,
        projection: (contactBidders, paidInvoices) => {
            return paidInvoices.map<InvoiceInfo>(inv => {
                return {
                    ...inv,
                    contactBidder: contactBidders[inv.contactId],
                    payments: []
                }
            });
        }
    });
}

// TODO: Filtering for paid vs unpaid should happen in front-end, not back-end

export const useInvoiceDataGetPaidInvoicesQuery = () => {
    const currentSale = useCurrentSale();

    const getContactBiddersQuery = useBidderDataGetContactBiddersQuery();

    const getPaidInvoicesQuery = useAgTechQuery({
        query: InvoiceDataGetPaidInvoicesQuery,
        queryContext: { saleId: currentSale.id }
    });

    return useAgTechQueryResultCombination({
        firstQuery: getContactBiddersQuery,
        secondQuery: getPaidInvoicesQuery,
        projection: (contactBidders, paidInvoices) => {
            return paidInvoices.map<InvoiceInfo>(inv => {
                return {
                    ...inv,
                    contactBidder: contactBidders[inv.contactId],
                    payments: []
                }
            });
        }
    });
}

// TODO: Should pull from larger list queries to reduce duplication

export const useInvoiceDataGetInvoiceDetailsQuery = (props: { invoiceId: number }) => {
    const currentSale = useCurrentSale();

    const invoiceDetailsQuery = useAgTechQuery({
        query: InvoiceDataGetInvoiceDetailQuery,
        queryContext: { saleId: currentSale.id, invoiceId: props.invoiceId }
    });

    const contactDetailsQuery = useAgTechQuery({
        query: SaleContactDataGetContactDetailsQuery,
        queryContext: {
             contactId: invoiceDetailsQuery.queryResult.data?.contactId ?? 0
        },
        settings: {
            enabled: invoiceDetailsQuery.queryResult.hasSucceeded
        }
    });

    const invoiceContactDetails = useAgTechQueryResultCombination({
        firstQuery: invoiceDetailsQuery,
        secondQuery: contactDetailsQuery,
        projection: (invoiceDetails, contactDetails) => ({
            invoice: {
                id: invoiceDetails.invoiceId,
                status: invoiceDetails.isPayed ? 'Paid' : 'Unpaid',
                timestamp: invoiceDetails.timestamp,
                info: {
                    date: invoiceDetails.invoiceDate,
                    number: invoiceDetails.invoiceNumber,
                    total: invoiceDetails.invoiceTotal,
                    status: invoiceDetails.isPayed
                        ? InvoiceStatus.Paid
                        : invoiceDetails.payments.length > 0
                            ? InvoiceStatus["Partially Paid"]
                            : InvoiceStatus.Unpaid
                },
                details: {
                    creditsApplied: invoiceDetails.credits,
                    InvoiceModifiersGiven: invoiceDetails.invoiceModifiers,
                    lotsPurchased: invoiceDetails.lots,
                    paymentsMade: invoiceDetails.payments
                }
            },
            contact: {
                id: contactDetails.contact.id,
                info: contactDetails.contact,
                details: {
                    notes: Object.values(contactDetails.notes),
                    credits: getAvailableCreditsForInvoice(props.invoiceId, Object.values(contactDetails.credits))
                }
            }
        })
    });

    const lotDataQuery = useAgTechQuery({
        query: LotDataGetLotsQuery,
        queryContext: { saleId: currentSale.id },
    });

    return useAgTechQueryResultCombination({
        firstQuery: invoiceContactDetails,
        secondQuery: lotDataQuery,
        projection: (invoiceDetails, lots) => {
            return {
                invoice: {
                    ...invoiceDetails.invoice,
                    details: {
                        ...invoiceDetails.invoice.details,
                        lotsPurchased: invoiceDetails.invoice.details.lotsPurchased.map(lot => lots[lot.lotId])
                    }
                },
                contact: invoiceDetails.contact,
                lots: lots
            }
        }
    });
}

/// Downloads

export const useInvoiceDataDownloadSampleInvoiceRequest = (): AgTechHttpRequest<any, any> => {
    let sampleInvoiceDownloadRequest = useAgTechApiDownloadRequest();

    return {
        execute: async () => await sampleInvoiceDownloadRequest.download({
            path: 'Invoice/GetSampleInvoiceDocument/',
            fileNameWithExtension: `Sample_Invoice_T1_Test_${new Date().toDateString()}.pdf`
        })
    }
}

/// Helpers

export const invalidateInvoiceQueries = (
    props: { 
        invalidateQuery: <TQueryInputData, >(props: AgTechInvalidateQueryDataProps<TQueryInputData>) => void
    },
    context: {
        saleId: number,
        invoiceId: number
    }
) => {
    props.invalidateQuery({
        query: InvoiceDataGetInvoiceDetailQuery,
        queryContext: { saleId: context.saleId, invoiceId: context.invoiceId }
    });

    props.invalidateQuery({
        query: InvoiceDataGetInvoiceDocumentQuery,
        queryContext: { saleId: context.saleId, invoiceId: context.invoiceId }
    });

    props.invalidateQuery({
        query: InvoiceDataGetUnpaidInvoicesQuery,
        queryContext: { saleId: context.saleId }
    });
}